import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import ThemedTable from "../../components/ThemedTable";
import Loader from "../../components/Loader";
import {
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { apiClient } from "../../apiclient/apiclient";
import Pagination from "../../components/Pagination";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse } from "date-fns";
import {
  formatAnyDateToDDMMYYYY,
  parseDDMMYYYY as utilParseDDMMYYYY,
  formatDDMMYYYY as utilFormatDDMMYYYY,
  formatDDMMYYYYtoISO as utilFormatDDMMYYYYtoISO,
} from "../../utils/dateFormatter";
import PageContainer from "../../components/PageContainer";
import { Download, Search, RotateCcw } from "lucide-react";

// dd-MM-yyyy -> Date (local version used for DatePicker)
const parseDDMMYYYY = (value) => {
  if (!value) return null;
  return parse(value, "dd-MM-yyyy", new Date());
};

// Date -> dd-MM-yyyy (local version)
const formatDDMMYYYY = (date) => {
  if (!date) return "";
  return format(date, "dd-MM-yyyy");
};

// dd-MM-yyyy -> yyyy-mm-dd (for API)
const formatDDMMYYYYtoISO = (ddmmyy) => {
  if (!ddmmyy) return "";
  const date = parseDDMMYYYY(ddmmyy);
  if (!date) return ddmmyy;
  return format(date, "yyyy-MM-dd");
};

const getStatusBadge = (status) => {
  const styles = {
    pending: "bg-yellow-100 text-yellow-700",
    processing: "bg-blue-100 text-blue-700", // shipped
    shipped: "bg-blue-100 text-blue-700",
    delivered: "bg-purple-100 text-purple-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return styles[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
};

const formatCurrency = (val) => {
  const n = Number(val || 0);
  return (
    "₹ " +
    n.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })
  );
};

const SalesReport = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [cards, setCards] = useState({});
  const [chart, setChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [table, setTable] = useState([]);
  const [returnsTable, setReturnsTable] = useState([]);
  const [subTab, setSubTab] = useState("sales"); // "sales" | "returns"
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(200);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedOrderItems, setSelectedOrderItems] = useState(null); // { orderId, clientName, date, items, totalAmount }

  /* ================= FETCH REPORT ================= */
  const fetchReport = async (startDateDisplay, endDateDisplay) => {
    try {
      setLoading(true);
      const startDate = startDateDisplay ? formatDDMMYYYYtoISO(startDateDisplay) : "";
      const endDate = endDateDisplay ? formatDDMMYYYYtoISO(endDateDisplay) : "";

      const res = await apiClient.get(`/daily-sales?start=${startDate}&end=${endDate}`);

      setCards(res.data.cards || {});
      setChart(res.data.chart || []);
      setTopProducts(res.data.topProducts || []);
      setTable(res.data.table || []);
      setReturnsTable(res.data.returnsTable || []);
    } catch (err) {
      console.error("Failed to fetch report:", err);
    } finally {
      setLoading(false);
    }
  };

  const safeLower = (value) => String(value ?? "").toLowerCase();

  const getProductSearchText = (productName) => {
    if (Array.isArray(productName)) {
      return productName.join(" ");
    }
    return productName;
  };

  const filteredTable = useMemo(() => {
    return table.filter((row) => {
      const q = safeLower(search);
      return (
        safeLower(row.orderId).includes(q) ||
        safeLower(row.clientName).includes(q) ||
        safeLower(getProductSearchText(row.productName)).includes(q) ||
        safeLower(row.orderStatus).includes(q)
      );
    });
  }, [table, search]);

  const filteredReturns = useMemo(() => {
    return returnsTable.filter((row) => {
      const q = safeLower(search);
      return (
        safeLower(row.returnId).includes(q) ||
        safeLower(row.clientName).includes(q) ||
        safeLower(row.originalOrderId).includes(q) ||
        safeLower(row.notes).includes(q)
      );
    });
  }, [returnsTable, search]);

  const totalPages = Math.ceil(
    (subTab === "sales" ? filteredTable.length : filteredReturns.length) / pageSize
  );
  
  const paginatedTable = useMemo(() => {
    const list = subTab === "sales" ? filteredTable : filteredReturns;
    return list.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [subTab, filteredTable, filteredReturns, currentPage, pageSize]);

  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);

    const fromDisplay = formatDDMMYYYY(lastWeek);
    const toDisplay = formatDDMMYYYY(today);

    setFrom(fromDisplay);
    setTo(toDisplay);
    fetchReport(fromDisplay, toDisplay);
  }, []);

  /* ================= DOWNLOAD ================= */
  const downloadExcel = async () => {
    try {
      setDownloading(true);
      const res = await apiClient.get(
        `/daily-sales?start=${formatDDMMYYYYtoISO(from)}&end=${formatDDMMYYYYtoISO(to)}&download=true&search=${search}`,
        { responseType: "blob" },
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "DailySalesReport.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      let errorMsg = "Failed to download report due to a network error.";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.message) errorMsg = parsed.message;
        } catch (e) {
          // Fallback if blob text is not valid JSON
        }
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      alert(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Sales Overview</h1>
          <p className="text-sm text-slate-500 mt-1">
            Weekly trend by default • Live sales insights
          </p>
        </div>
      </div>

      {/* ================= KPI CARDS ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {/* TODAY */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-e border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            Today's Sales
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
            {formatCurrency(cards.todaySales)}
          </h2>
          <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">
            {cards.todayOrders || 0} orders
          </p>
        </div>

        {/* WEEK */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 border-e border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            Last 7 Days
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
            {formatCurrency(cards.weekSales)}
          </h2>
          <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">
            {cards.weekOrders || 0} orders
          </p>
        </div>

        {/* MONTH */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-e border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            This Month
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
            {formatCurrency(cards.monthSales)}
          </h2>
          <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">
            {cards.monthOrders || 0} orders
          </p>
        </div>

        {/* TOP PRODUCT */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-cyan-500 to-sky-600 border-b flex flex-col justify-between">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <div>
            <p className="text-sm opacity-80 font-semibold uppercase tracking-wider relative z-10">
              Top Product
            </p>
            <h2 className="text-lg font-bold mt-2 truncate relative z-10">
              {cards.topProduct || "N/A"}
            </h2>
          </div>
          <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">
            {cards.topProductQty || 0} units sold
          </p>
        </div>
      </div>

      {/* ================= RANGE SUMMARY BAR ================= */}
      {(() => {
        const rangeGrossSales = table.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
        const rangeReturns = cards.returnsAmount || 0;
        const rangeNetSales = rangeGrossSales - rangeReturns;

        return (
          <div className="grid grid-cols-1 md:grid-cols-3 bg-white border-b border-slate-200">
            {/* GROSS CARD */}
            <div className="py-8 px-6 border-b md:border-b-0 md:border-e border-slate-200 flex flex-col justify-center">
              <p className="text-xs md:text-[13px] font-bold text-slate-400 uppercase tracking-wider">
                Selected Range Gross Sales
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-slate-800 mt-2 font-mono">
                {formatCurrency(rangeGrossSales)}
              </h3>
            </div>
            {/* RETURNS CARD */}
            <div className="py-8 px-6 border-b md:border-b-0 md:border-e border-slate-200 flex flex-col justify-center">
              <p className="text-xs md:text-[13px] font-bold text-slate-400 uppercase tracking-wider">
                Selected Range Returns (Credit Notes)
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-red-600 mt-2 font-mono">
                -{formatCurrency(rangeReturns)}
              </h3>
            </div>
            {/* NET CARD */}
            <div className="py-8 px-6 flex flex-col justify-center">
              <p className="text-xs md:text-[13px] font-bold text-[#172554] uppercase tracking-wider">
                Selected Range Net Sales
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-green-600 mt-2 font-mono">
                {formatCurrency(rangeNetSales)}
              </h3>
            </div>
          </div>
        );
      })()}

      {/* ================= CHARTS SIDE BY SIDE ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* SALES TREND CHART */}
        <div className="bg-white border-b border-e p-4 flex flex-col">
          <div className="flex items-center justify-between mb-6 px-2">
            <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
              Sales Trend (Selected Range)
            </h3>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#172554]"></div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Revenue
              </span>
            </div>
          </div>

          <div className="h-[300px] flex items-center justify-center">
            {loading ? (
              <Loader text="Loading chart data..." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F69314" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F69314" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={{ stroke: "#f1f5f9" }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-[#172554] text-white rounded-lg px-4 py-3 shadow-2xl border border-white/10">
                          <p className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1">
                            {label}
                          </p>
                          <p className="text-base font-black italic">
                            ₹{payload[0].value.toLocaleString()}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="none"
                    fillOpacity={1}
                    fill="url(#colorSales)"
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#172554"
                    strokeWidth={3}
                    dot={{
                      r: 4,
                      fill: "#172554",
                      strokeWidth: 2,
                      stroke: "#fff",
                    }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* TOP SELLING PRODUCTS */}
        <div className="bg-white border-b p-4 flex flex-col">
          <div className="flex items-center justify-between mb-6 px-2">
            <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
              Top Selling Products
            </h3>
            <Link
              to="/reports/top-products"
              className="text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline uppercase tracking-wider"
              title="View Top Selling Products Report"
            >
              View More
            </Link>
          </div>

          <div className="h-[300px] flex items-center justify-center">
            {loading ? (
              <Loader text="Loading chart data..." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topProducts}
                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                  barCategoryGap="25%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={{ stroke: "#f1f5f9" }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "#f8fafc" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                            {payload[0].payload.name}
                          </p>
                          <p className="text-lg font-black text-[#172554]">
                            {payload[0].value}{" "}
                            <span className="text-xs text-slate-300 font-normal tracking-wide">
                              Units Sold
                            </span>
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="quantity" radius={[4, 4, 0, 0]} barSize={40}>
                    {topProducts.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index % 2 === 0 ? "#172554" : "#F69314"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* ================= CONTROL BAR (LIGHT BLUE SECTION) ================= */}
      <div className="border-t border-b bg-[#EFF6FF] p-3">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {/* FROM */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              From
            </label>
            <DatePicker
              selected={parseDDMMYYYY(from)}
              onChange={(date) => setFrom(formatDDMMYYYY(date))}
              dateFormat="dd-MM-yyyy"
              placeholderText="DD-MM-YYYY"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-40 shadow-sm"
            />
          </div>

          {/* TO */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
              To
            </label>
            <DatePicker
              selected={parseDDMMYYYY(to)}
              onChange={(date) => setTo(formatDDMMYYYY(date))}
              dateFormat="dd-MM-yyyy"
              placeholderText="DD-MM-YYYY"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-40 shadow-sm"
            />
          </div>

          {/* SEARCH */}
          <div className="relative w-full sm:flex-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-slate-400" />
            </span>
            <input
              type="text"
              placeholder="Search by ID, Client, Product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm"
            />
          </div>

          {/* ACTIONS */}
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => fetchReport(from, to)}
              disabled={loading}
              className="w-9 h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
              title="View Report"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Search size={18} />
              )}
            </button>

            <button
              onClick={downloadExcel}
              disabled={downloading}
              className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
              title="Download Excel"
            >
              {downloading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Download size={18} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ================= TABLE SWITCH TOGGLE ================= */}
      <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-center">
        <div className="flex items-center gap-4 select-none">
          {/* Sales Details Text Label */}
          <span
            onClick={() => {
              setSubTab("sales");
              setCurrentPage(1);
            }}
            className={`text-sm cursor-pointer transition-all duration-200 ${
              subTab === "sales"
                ? "font-bold text-slate-800"
                : "text-slate-400 hover:text-slate-600 font-semibold"
            }`}
          >
            Sales Details ({filteredTable.length})
          </span>

          {/* Custom Capsule Toggle Switch */}
          <div
            onClick={() => {
              const nextTab = subTab === "sales" ? "returns" : "sales";
              setSubTab(nextTab);
              setCurrentPage(1);
            }}
            className="w-[50px] h-[26px] bg-slate-200/80 hover:bg-slate-200 rounded-full p-[3px] cursor-pointer transition-all duration-300 flex items-center relative border border-slate-300/40"
          >
            <div
              className={`w-5 h-5 bg-orange-500 rounded-full shadow transition-all duration-300 transform ${
                subTab === "returns" ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </div>

          {/* Returns Details Text Label */}
          <span
            onClick={() => {
              setSubTab("returns");
              setCurrentPage(1);
            }}
            className={`text-sm cursor-pointer transition-all duration-200 ${
              subTab === "returns"
                ? "font-bold text-slate-800"
                : "text-slate-400 hover:text-slate-600 font-semibold"
            }`}
          >
            Returns & Credit Notes ({filteredReturns.length})
          </span>
        </div>
      </div>

      {/* ================= SALES / RETURNS TABLE ================= */}
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200">
        {subTab === "sales" ? (
          <ThemedTable className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
                <th className="p-3 text-center w-10 hidden md:table-cell">#</th>
                <th className="p-3 text-left">Order Details</th>
                <th className="p-3 text-left">Client Name</th>
                <th className="p-3 text-left">Products Details</th>
                <th className="p-3 text-right">Billing Details</th>
                <th className="p-3 text-center">Statuses</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-20 text-center">
                    <Loader text="Loading sales data..." />
                  </td>
                </tr>
              ) : paginatedTable.length > 0 ? (
                paginatedTable.map((row, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50 transition">
                    <td className="px-2 py-3 text-center text-slate-400 font-medium hidden md:table-cell">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <Link
                        to={`/orders?search=${row.orderId}`}
                        className="font-bold text-[#172554] hover:underline block"
                      >
                        {row.orderId}
                      </Link>
                      <span className="text-[10px] text-slate-500 block mt-0.5 font-medium">
                        {formatAnyDateToDDMMYYYY(row.date)}
                      </span>
                    </td>
                    <td className="px-2 py-3 font-bold text-slate-700 whitespace-nowrap">
                      <div
                        className="max-w-[150px] truncate"
                        title={row.clientName}
                      >
                        {row.clientName}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-center whitespace-nowrap">
                      <button
                        onClick={() => setSelectedOrderItems({
                          orderId: row.orderId,
                          clientName: row.clientName,
                          date: row.date,
                          items: row.items,
                          totalAmount: row.totalAmount,
                          discount: row.discount,
                          paidAmount: row.paidAmount,
                          balanceAmount: row.balanceAmount,
                        })}
                        className="text-blue-600 font-bold hover:text-blue-800 hover:underline inline-flex items-center gap-1 bg-blue-50 px-2.5 py-1.5 rounded-md border border-blue-200/50 hover:bg-blue-100 transition-all text-xs"
                      >
                        <span>{row.items?.length || 0} Products</span>
                      </button>
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      <div className="font-bold text-slate-800">Total: ₹{row.totalAmount?.toLocaleString()}</div>
                      {row.discount > 0 && (
                        <div className="text-[10px] text-orange-500 font-semibold leading-tight mt-0.5">
                          Disc: -₹{row.discount?.toLocaleString()}
                        </div>
                      )}
                      <div className="text-[10px] text-green-600 font-semibold leading-tight mt-0.5">
                        Paid: ₹{row.paidAmount?.toLocaleString()}
                      </div>
                      {row.balanceAmount > 0 ? (
                        <div className="text-[10px] text-red-600 font-bold leading-tight mt-0.5">
                          Bal: ₹{row.balanceAmount?.toLocaleString()}
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 font-medium leading-tight mt-0.5">
                          Bal: ₹0
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3 text-center whitespace-nowrap">
                      <div className="flex flex-col gap-1.5 items-center justify-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider block w-fit ${
                            row.paymentStatus?.toLowerCase() === "paid"
                              ? "bg-green-100 text-green-700"
                              : "bg-orange-100 text-orange-700"
                          }`}
                        >
                          {row.paymentStatus}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider block w-fit ${getStatusBadge(
                            row.orderStatus,
                          )}`}
                        >
                          {row.orderStatus}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-20 text-center text-slate-400 font-medium italic">
                    No matching sales records found...
                  </td>
                </tr>
              )}
            </tbody>
          </ThemedTable>
        ) : (
          <ThemedTable className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
                <th className="p-3 text-center w-10">#</th>
                <th className="p-3 text-left">Credit Note</th>
                <th className="p-3 text-left">Reference & Client</th>
                <th className="p-3 text-left">Items & Reason</th>
                <th className="p-3 text-right">Refund Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center">
                    <Loader text="Loading returns data..." />
                  </td>
                </tr>
              ) : paginatedTable.length > 0 ? (
                paginatedTable.map((row, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50 transition">
                    <td className="px-2 py-3 text-center text-slate-400 font-medium">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="font-bold text-[#172554]">{row.returnId}</div>
                      <span className="text-[10px] text-slate-500 block mt-0.5 font-medium">
                        {formatAnyDateToDDMMYYYY(row.date)}
                      </span>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="text-slate-600 font-medium text-[11px] flex items-center gap-1">
                        <span>Ref:</span>
                        <Link
                          to={`/orders?search=${row.originalOrderId}`}
                          className="font-bold text-blue-600 hover:underline"
                        >
                          {row.originalOrderId}
                        </Link>
                      </div>
                      <div className="font-bold text-slate-700 mt-0.5" title={row.clientName}>
                        {row.clientName}
                      </div>
                    </td>
                    <td className="px-2 py-3">
                      <div className="font-semibold text-slate-600 text-[11px]">
                        {row.itemsCount} {row.itemsCount === 1 ? "Product" : "Products"}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate max-w-[200px] mt-0.5" title={row.notes}>
                        {row.notes ? `Note: ${row.notes}` : <span className="italic">No notes</span>}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right font-black text-red-600 whitespace-nowrap">
                      -₹{row.returnAmount?.toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-slate-400 font-medium italic">
                    No returns processed in the selected date range.
                  </td>
                </tr>
              )}
            </tbody>
          </ThemedTable>
        )}
      </div>

      <div className="flex justify-center items-center py-8 bg-white border-t border-slate-100">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* ================= PRODUCTS DETAIL MODAL ================= */}
      {selectedOrderItems && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* HEADER */}
            <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span>Order Items:</span>
                <span className="text-blue-100 font-mono font-black bg-[#1e2f6a]/80 px-2 py-0.5 rounded border border-blue-900 text-xs">
                  {selectedOrderItems.orderId}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => setSelectedOrderItems(null)}
                className="text-xl text-white hover:opacity-85 transition-opacity"
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200">
              {/* Order Context Details */}
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-3 flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-slate-600 font-medium">
                <div>
                  Client: <span className="font-bold text-slate-800">{selectedOrderItems.clientName}</span>
                </div>
                <div>
                  Date: <span className="font-bold text-slate-800">{formatAnyDateToDDMMYYYY(selectedOrderItems.date)}</span>
                </div>
              </div>

              {/* Table details */}
              <div className="overflow-x-auto w-full">
                <table className="w-full text-[13px] border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] font-bold border-b border-slate-200">
                      <th className="p-3 px-6 text-center w-12">#</th>
                      <th className="p-3 px-6 text-left">Product Name & Variant</th>
                      <th className="p-3 px-6 text-left">Reference</th>
                      <th className="p-3 px-6 text-left">Warehouse / Batch</th>
                      <th className="p-3 px-6 text-center w-16">Qty</th>
                      <th className="p-3 px-6 text-right">Base Price</th>
                      <th className="p-3 px-6 text-center w-16">GST %</th>
                      <th className="p-3 px-6 text-right">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrderItems.items?.length > 0 ? (
                      selectedOrderItems.items.map((item, idx) => {
                        const price = item.price || 0;
                        const gst = item.taxRate || item.productId?.taxRate || 0;
                        const isTaxInclusive = item.taxInclude ?? true;
                        const basePrice = isTaxInclusive ? (price * 100) / (100 + gst) : price;

                        return (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition last:border-b-0">
                            <td className="p-3 px-6 text-center text-slate-400 font-semibold">{idx + 1}</td>
                            <td className="p-3 px-6">
                              <div className="font-bold text-slate-800">{item.productName || item.name}</div>
                              <div className="text-[10px] text-slate-500 font-semibold mt-0.5">
                                Pack: {item.quantityValue} {item.quantityUnit}
                              </div>
                            </td>
                            <td className="p-3 px-6">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${item.staffReference?.name ? "bg-purple-50 text-purple-700 border border-purple-100" : "bg-slate-100 text-slate-600"}`}>
                                {item.staffReference?.name || "Direct Sales"}
                              </span>
                            </td>
                            <td className="p-3 px-6">
                              <div className="flex flex-col gap-1 text-[11px]">
                                {item.warehouseName && (
                                  <div>
                                    <span className="text-slate-400">Warehouse: </span>
                                    <span className="font-bold text-slate-700">{item.warehouseName}</span>
                                  </div>
                                )}
                                {(item.batchId || item.expiryDate) && (
                                  <div className="flex flex-wrap gap-x-2 text-[10px] text-slate-500 font-medium">
                                    {item.batchId && <span>Batch: <span className="font-mono">{item.batchId}</span></span>}
                                    {item.expiryDate && <span>Exp: {formatAnyDateToDDMMYYYY(item.expiryDate)}</span>}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-3 px-6 text-center font-bold text-slate-700">{item.quantity}</td>
                            <td className="p-3 px-6 text-right font-semibold text-slate-600">
                              ₹{basePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-3 px-6 text-center text-slate-500 font-bold">{gst}%</td>
                            <td className="p-3 px-6 text-right font-black text-slate-800">
                              ₹{(price * (item.quantity || 0)).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8} className="p-6 text-center text-slate-400 italic">
                          No items details populated.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 bg-slate-50 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-slate-600 font-medium">
                <div className="text-slate-800">
                  Gross Value: <span className="font-bold text-sm">₹{selectedOrderItems.totalAmount?.toLocaleString()}</span>
                </div>
                {selectedOrderItems.discount > 0 && (
                  <div className="text-orange-600">
                    Discount: <span className="font-bold">-₹{selectedOrderItems.discount?.toLocaleString()}</span>
                  </div>
                )}
                <div>
                  Paid Amount: <span className="font-bold text-green-700 text-sm">₹{selectedOrderItems.paidAmount?.toLocaleString()}</span>
                </div>
                {selectedOrderItems.balanceAmount > 0 && (
                  <div>
                    Outstanding Balance: <span className="font-bold text-red-600 text-sm">₹{selectedOrderItems.balanceAmount?.toLocaleString()}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedOrderItems(null)}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SalesReport;
